import React from 'react';
import moment from 'moment';

import { trackAnalysisShowClipsWarning } from '../../../analytics';

import { URL } from '../../../constants';
import {
  Event,
  AttendeeReviewData,
  Person,
  HlReel,
  Shot,
  AnalysisClips,
} from '../../../types';
import {
  isEmpty,
  isPlural,
  getUrl,
  getPluralWord,
  getPluralPhrase,
} from '../../../utils';

const getCountText = (count: number, totalCount: number) => {
  return count === totalCount ? 'all' : `${count} of ${totalCount}`;
};

export const getSelectText = (props: {
  attendeeReviewData?: AttendeeReviewData;
  isAssetsUploaded: boolean;
  isDisableUpload: boolean;
}) => {
  const { attendeeReviewData, isAssetsUploaded, isDisableUpload } = props;

  const isAttendeeReview = attendeeReviewData?.isAttendeeReview;
  const isRemindLater = attendeeReviewData?.isRemindLater;

  const releaseDate = moment(attendeeReviewData?.releaseDate ?? '');
  const now = moment().utc();
  const diffInHours = releaseDate.diff(now, 'hours');

  const subheading = isAssetsUploaded
    ? 'All assets are uploaded'
    : 'Upload your highlight reel template, some clips and tell us the people to identify in your clips';

  // analysis has already started
  if (isDisableUpload)
    return {
      subheading,
      alertText: (
        <>
          Assets can no longer be uploaded since the analysis has already
          started.
        </>
      ),
    };

  // is attendee review some assets are missing
  if (isAttendeeReview && !isAssetsUploaded) {
    // release date passed
    if (now > releaseDate)
      return {
        subheading,
        alertText: (
          <>
            The release date has passed, but some assets are still missing, so
            the analysis couldn’t begin.
          </>
        ),
      };
    // there's less than a day to release date
    else if (diffInHours <= 24)
      return {
        subheading,
        alertText: (
          <>
            Analysis starts in{' '}
            <b>
              {diffInHours === 0
                ? 'less than an hour'
                : getPluralPhrase(diffInHours, 'hour')}
            </b>
            , while some assets are still missing.
          </>
        ),
      };
  }

  return {
    subheading,
    alertText: null,
    isAllowRemindLater:
      isAttendeeReview &&
      !isRemindLater &&
      !isAssetsUploaded &&
      diffInHours > 49,
  };
};

// ANALYSIS

export const getAnalyseText = (props: {
  hlReel: HlReel | null;
  clips: AnalysisClips | null;
  peopleCount: number;
}) => {
  const { hlReel, clips, peopleCount } = props;

  if (!clips) return { analyseText: null, progress: 0 };

  const { newClips, ...rest } = clips;

  const PROGRESS_WEIGHTS = {
    convertingClips: 30,
    analysingClips: 50,
    finishedClips: 100,
    errorClips: 100,
  };

  const HIGHLIGHT_REEL_UNIT_COUNT = 5;
  const clipsCount = Object.values(rest).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );
  const totalUnits = clipsCount + HIGHLIGHT_REEL_UNIT_COUNT;

  const clipProgress = Object.entries(PROGRESS_WEIGHTS).reduce(
    (sum, [key, weight]) =>
      sum + clips[key as keyof typeof PROGRESS_WEIGHTS].length * weight,
    0,
  );
  const hlReelProgress = HIGHLIGHT_REEL_UNIT_COUNT * (hlReel?.shots ? 100 : 30);
  const progress = Math.round((clipProgress + hlReelProgress) / totalUnits);

  return {
    analyseText:
      progress < 100 ? (
        <>
          Our AI is analysing a highlight reel and looking for{' '}
          <b>{getPluralPhrase(peopleCount, 'person')}</b> from your{' '}
          <b>{getPluralPhrase(clipsCount, 'clip')}</b>.
        </>
      ) : (
        <>
          Analysis of <b>{getPluralPhrase(clipsCount, 'clip')}</b> for{' '}
          <b>{getPluralPhrase(peopleCount, 'person')}</b> done.
          {!isEmpty(newClips) && (
            <>
              <br />
              Pending analysis of{' '}
              <b>{getPluralPhrase(newClips, 'clip', 'new')}</b>.
            </>
          )}
        </>
      ),
    progress,
  };
};

export const getReviewText = (props: {
  currentProject: Event | null;
  hlReel: HlReel | null;
  requiredShots: Shot[];
  completePeople: Person[];
  incompletePeople: Person[];
  generatedPeople: Person[];
  selectedPeopleIds?: string[];
  isAnalysisPage?: boolean;
  isSample: boolean;
}) => {
  const {
    currentProject,
    hlReel,
    requiredShots,
    completePeople,
    incompletePeople,
    generatedPeople,
    selectedPeopleIds = [],
    isAnalysisPage = false,
    isSample,
  } = props;

  const eventId = currentProject?.eventId ?? '';

  const allPeopleCount =
    completePeople.length + incompletePeople.length + generatedPeople.length;

  // review is complete
  if (!isAnalysisPage && currentProject?.stage === 'generation')
    return {
      reviewText: (
        <>
          Review of <b>{getPluralPhrase(allPeopleCount, 'person')}</b> done.
        </>
      ),
    };

  // if highlight reel is processed and is invalid (brand events)
  if (hlReel?.status === 'invalid')
    return {
      reviewAlert: (
        <>
          <b>Your highlight reel has some missing wildcards.</b>
          {!isAnalysisPage ? (
            <>
              <br />
              Review the results for more details.
            </>
          ) : (
            <>
              {' '}
              Re-upload the highlight reel{' '}
              <a href={getUrl(URL.VB_HL_REEL, eventId)}>here</a> to be able to
              proceed.
            </>
          )}
        </>
      ),
    };

  const completeCount = completePeople?.length || 0;

  // if there's NOT enough clips for at least one person
  if (completeCount === 0) {
    // add GA event
    const projectsUpd = JSON.parse(
      localStorage.getItem('clipsWarningProjects') ?? '[]',
    );

    // check if eventId is not in localStorage clipsWarningProjects – to avoid duplicate events
    if (!projectsUpd.includes(eventId)) {
      trackAnalysisShowClipsWarning(isSample);

      projectsUpd.push(eventId);
      localStorage.setItem('clipsWarningProjects', JSON.stringify(projectsUpd));
    }

    return {
      reviewAlert: (
        <>
          <b>Not enough clips</b>. Minimum of{' '}
          {getPluralPhrase(requiredShots, 'clip', 'matching')}{' '}
          {isPlural(requiredShots) ? 'are' : 'is'} required to create a
          personalised highlight reel.
          {!isAnalysisPage && (
            <>
              <br />
              Review the results for more details.
            </>
          )}
        </>
      ),
    };
  }

  // to generate text
  let toGenerateCount = 0;
  completePeople.forEach((p) => {
    if (selectedPeopleIds.includes(p.face.elementId)) toGenerateCount++;
  });

  // if there's enough clips for at least one person
  return {
    reviewText: (
      <>
        Our AI found enough clips to generate personalised highlight reels for{' '}
        <b>{getCountText(completeCount, allPeopleCount)} checked-in people</b>.
        {!isAnalysisPage && (
          <>
            <br />
            Review the results to continue.
          </>
        )}
      </>
    ),
    toGenerateText: getCountText(toGenerateCount, allPeopleCount),
  };
};

// GENERATION

export const getGenerateText = (props: {
  completePeople: Person[];
  incompletePeople: Person[];
  generatedPeople: Person[];
  toGeneratePeopleIds: string[];
}) => {
  const {
    completePeople,
    incompletePeople,
    generatedPeople,
    toGeneratePeopleIds,
  } = props;

  const generatedPeopleIds = generatedPeople.map((p) => p.face.elementId);
  const completePeopleIds = completePeople.map((p) => p.face.elementId);
  const generatingPeople = toGeneratePeopleIds.filter(
    (id) => completePeopleIds.includes(id) && !generatedPeopleIds.includes(id),
  );

  const completeCount = completePeople.length || 0;
  const incompleteCount = incompletePeople.length || 0;

  const toGenerateCount = toGeneratePeopleIds.length || 0;
  const generatingCount = generatingPeople.length || 0;
  const generatedCount = generatedPeople.length || 0;

  const allCount = completeCount + incompleteCount + generatedCount;

  const progress = Math.round(
    ((generatedCount + 0.5) / (generatedCount + generatingCount)) * 100,
  );

  if (progress < 100)
    return {
      generateText: (
        <>
          Generating Highlight Reel{isPlural(toGenerateCount)} for{' '}
          <b>
            {getCountText(toGenerateCount, allCount)}{' '}
            {getPluralWord(allCount, 'person')}
          </b>
          .
        </>
      ),
      progress,
    };

  return {
    generateText: (
      <>
        Generation of <b>{getPluralPhrase(generatedCount, 'Highlight Reel')}</b>{' '}
        done.
      </>
    ),
    progress: 100,
  };
};

export const getShareText = (peopleCount: number) => {
  if (peopleCount === 0)
    return { shareText: <>Share out Personalised Highlight Reels</> };

  if (peopleCount === 1)
    return {
      shareText: (
        <>Personalised Highlight Reel is ready to share with 1 person!</>
      ),
    };

  return {
    shareText: (
      <>
        Personalised Highlight Reels are ready to share with {peopleCount}{' '}
        people!
      </>
    ),
  };
};
